/* iSpeedToClose-managed phone numbers — search, buy, list, release.
 *
 * Surfaces in Settings → Twilio, BELOW the existing BYOA TwilioCard so users
 * can still bring their own Twilio account if they prefer. The two flows
 * coexist: a user might have ONE managed number (for cold outreach) and
 * still hook up their personal Twilio (for their existing relationships).
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Phone,
  Search,
  Loader2,
  Check,
  Trash2,
  EyeOff,
  PhoneIncoming,
  MessageSquare,
  Image as ImageIcon,
  Sparkles,
} from "lucide-react";
import A2pActivationPanel from "@/components/A2pActivationPanel";

function CapabilityChip({ enabled, label, Icon }) {
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${
        enabled
          ? "bg-emerald-100 text-emerald-800 border-emerald-200"
          : "bg-stone-100 text-stone-500 border-stone-200"
      }`}
    >
      <Icon size={9} /> {label}
    </span>
  );
}

export default function ManagedNumbersCard() {
  const [configured, setConfigured] = useState(null); // null=loading, false=hide, true=show
  const [owned, setOwned] = useState([]);
  const [areaCode, setAreaCode] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [buying, setBuying] = useState(null);
  const [releasing, setReleasing] = useState(null);
  const [hiding, setHiding] = useState(null);

  const loadOwned = async () => {
    try {
      const { data } = await api.get("/twilio-numbers/owned");
      setOwned(data?.items || []);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 402 || code === 403) setConfigured(false); // hide for free tier
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/twilio-numbers/configured");
        setConfigured(!!data?.configured);
        if (data?.configured) await loadOwned();
      } catch (e) {
        const code = e?.response?.status;
        if (code === 402 || code === 403) setConfigured(false);
        else setConfigured(false);
      }
    })();
  }, []);

  const search = async (e) => {
    e?.preventDefault?.();
    setSearching(true);
    setResults([]);
    try {
      const { data } = await api.get("/twilio-numbers/search", {
        params: areaCode ? { area_code: areaCode, limit: 10 } : { limit: 10 },
      });
      setResults(data?.results || []);
      if (!data?.results?.length) {
        toast.info("No numbers found for that area code. Try a different one.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed.");
    } finally {
      setSearching(false);
    }
  };

  const buy = async (phoneNumber) => {
    if (!window.confirm(
      `Purchase ${phoneNumber} for $5/mo?\n\n` +
      `It will be billed on your next iSpeedToClose invoice (prorated for ` +
      `the rest of this month). You can release it anytime.`
    )) return;
    setBuying(phoneNumber);
    try {
      await api.post("/twilio-numbers/purchase", { phone_number: phoneNumber });
      toast.success(`${phoneNumber} is yours — ready to use!`);
      setResults((r) => r.filter((n) => n.phone_number !== phoneNumber));
      await loadOwned();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purchase failed.");
    } finally {
      setBuying(null);
    }
  };

  const release = async (item) => {
    if (!window.confirm(
      `Release ${item.phone_number}?\n\n` +
      `The number goes back to Twilio's pool and Stripe will credit you ` +
      `for the unused portion of the month. THIS IS IRREVERSIBLE — you ` +
      `may not be able to get this exact number back.`
    )) return;
    setReleasing(item.twilio_phone_sid);
    try {
      await api.delete(`/twilio-numbers/${item.twilio_phone_sid}`);
      toast.success(`${item.phone_number} released.`);
      await loadOwned();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Release failed.");
    } finally {
      setReleasing(null);
    }
  };

  const hide = async (item) => {
    // Soft-archive: DB-only cleanup for duplicate/malformed rows. Does NOT
    // touch Twilio, so the underlying number keeps working if another row
    // still references it. Use this to clean up legacy inserts that
    // dropped the +1 country code prefix, etc.
    if (!window.confirm(
      `Hide ${item.phone_number} from this list?\n\n` +
      `Use this for duplicate or malformed rows (e.g. missing +1 prefix). ` +
      `Your Twilio number itself is NOT released — it stays yours and keeps ` +
      `receiving inbound SMS on the properly-formatted row.`
    )) return;
    setHiding(item.twilio_phone_sid);
    try {
      await api.delete(
        `/twilio-numbers/number-brand/${encodeURIComponent(item.phone_number)}`,
      );
      toast.success(`${item.phone_number} hidden from list.`);
      await loadOwned();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hide failed.");
    } finally {
      setHiding(null);
    }
  };

  // Loading state.
  if (configured === null) return null;

  // Operator hasn't configured master Twilio yet — coming-soon state.
  if (configured === false) {
    return (
      <div className="bg-white rounded-xl border border-[var(--line)] p-5 mt-4"
           data-testid="managed-numbers-card-coming-soon">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-violet-50 border border-violet-200 flex items-center justify-center shrink-0">
            <Sparkles size={18} className="text-violet-600" />
          </div>
          <div>
            <div className="font-display font-extrabold text-lg leading-tight">
              iSpeedToClose Managed Numbers
            </div>
            <div className="text-xs text-[var(--ink-2)] mt-1 max-w-md">
              Buy a US local number directly from iSpeedToClose for <b>$5/mo</b> — no
              Twilio account setup required. Coming soon.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-[var(--line)] p-5 mt-4"
         data-testid="managed-numbers-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-violet-50 border border-violet-200 flex items-center justify-center shrink-0">
          <Phone size={18} className="text-violet-600" />
        </div>
        <div className="flex-1">
          <div className="font-display font-extrabold text-lg leading-tight">
            iSpeedToClose Managed Numbers
          </div>
          <div className="text-xs text-[var(--ink-2)] mt-0.5">
            Pick a US local number — <b>$5/mo</b>, prorated, release anytime.
            SMS + voice + MMS included. No Twilio setup required.
          </div>
        </div>
      </div>

      {/* Owned numbers */}
      {owned.length > 0 && (
        <div className="mb-5">
          <div className="text-[10px] uppercase tracking-wider text-[var(--ink-2)] font-bold mb-2">
            Your numbers
          </div>
          <div className="space-y-2">
            {owned.map((n) => {
              // Malformed = not E.164 (US should start with +1 and be 12 chars total).
              const malformed = !/^\+1\d{10}$/.test(n.phone_number || "");
              return (
              <div key={n.twilio_phone_sid}
                   className={`border rounded-lg px-3 py-2 flex items-center justify-between gap-3 ${
                     malformed
                       ? "border-rose-200 bg-rose-50/40"
                       : "border-[var(--line)]"
                   }`}
                   data-testid={`owned-number-${n.twilio_phone_sid}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-mono text-sm font-semibold">{n.phone_number}</div>
                    {malformed && (
                      <span className="text-[9.5px] uppercase tracking-widest font-black rounded-full px-1.5 py-0.5 border bg-rose-100 text-rose-800 border-rose-200"
                            data-testid={`malformed-badge-${n.twilio_phone_sid}`}>
                        Malformed
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 mt-1">
                    <CapabilityChip enabled={!!n.capabilities?.voice} label="Voice" Icon={PhoneIncoming} />
                    <CapabilityChip enabled={!!n.capabilities?.sms} label="SMS" Icon={MessageSquare} />
                    <CapabilityChip enabled={!!n.capabilities?.mms} label="MMS" Icon={ImageIcon} />
                    <span className="text-[10px] text-[var(--ink-2)] ml-2">${n.monthly_price_usd}/mo</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => hide(n)}
                    disabled={hiding === n.twilio_phone_sid}
                    className="text-slate-600 hover:bg-slate-100 px-2 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1"
                    data-testid={`hide-${n.twilio_phone_sid}`}
                    title="Hide this row from the list (safe — does not release the Twilio number)"
                  >
                    {hiding === n.twilio_phone_sid
                      ? <Loader2 size={12} className="animate-spin" />
                      : <EyeOff size={12} />}
                    Hide
                  </button>
                  <button
                    type="button"
                    onClick={() => release(n)}
                    disabled={releasing === n.twilio_phone_sid || malformed}
                    className="text-rose-600 hover:bg-rose-50 px-2 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`release-${n.twilio_phone_sid}`}
                    title={
                      malformed
                        ? "Release disabled for malformed rows — use Hide instead to avoid affecting your real Twilio number"
                        : "Release this number back to Twilio's pool (IRREVERSIBLE)"
                    }
                  >
                    {releasing === n.twilio_phone_sid
                      ? <Loader2 size={12} className="animate-spin" />
                      : <Trash2 size={12} />}
                    Release
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Search */}
      <form onSubmit={search} className="flex gap-2 mb-3" data-testid="number-search-form">
        <input
          type="text"
          value={areaCode}
          onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, "").slice(0, 3))}
          placeholder="Area code (e.g. 512)"
          inputMode="numeric"
          className="input flex-1 text-sm"
          data-testid="number-search-area-code"
        />
        <button
          type="submit"
          disabled={searching}
          className="bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold px-4 py-2 rounded-lg inline-flex items-center gap-1.5"
          data-testid="number-search-submit"
        >
          {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          Search
        </button>
      </form>

      {/* Search results */}
      {results.length > 0 && (
        <div className="space-y-2" data-testid="number-search-results">
          {results.map((n) => (
            <div key={n.phone_number}
                 className="border border-[var(--line)] rounded-lg px-3 py-2 flex items-center justify-between gap-3"
                 data-testid={`number-result-${n.phone_number}`}>
              <div className="min-w-0 flex-1">
                <div className="font-mono text-sm font-semibold">{n.phone_number}</div>
                <div className="text-xs text-[var(--ink-2)]">
                  {[n.locality, n.region].filter(Boolean).join(", ") || "United States"}
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <CapabilityChip enabled={!!n.capabilities?.voice} label="Voice" Icon={PhoneIncoming} />
                  <CapabilityChip enabled={!!n.capabilities?.sms} label="SMS" Icon={MessageSquare} />
                  <CapabilityChip enabled={!!n.capabilities?.mms} label="MMS" Icon={ImageIcon} />
                </div>
              </div>
              <button
                type="button"
                onClick={() => buy(n.phone_number)}
                disabled={buying === n.phone_number}
                className="shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5"
                data-testid={`buy-${n.phone_number}`}
              >
                {buying === n.phone_number
                  ? <Loader2 size={12} className="animate-spin" />
                  : <Check size={12} />}
                Buy $5/mo
              </button>
            </div>
          ))}
        </div>
      )}

      {/* A2P 10DLC activation panel — only renders when user owns ≥1 number. */}
      <A2pActivationPanel ownedCount={owned.length} />
    </div>
  );
}
